import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Upload, Save, Trash2, Palette, Image as ImageIcon } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function AssetUploader({ label, assetKey, hasAsset, accept, onChange }) {
  const { getHeaders } = useAuth();
  const [uploading, setUploading] = useState(false);
  const previewUrl = hasAsset ? `${API}/branding/platform/${assetKey}?v=${Date.now()}` : null;

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be under 2 MB');
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      await axios.post(`${API}/super-admin/branding/platform/${assetKey}`, fd, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${label} updated`);
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    setUploading(true);
    try {
      await axios.delete(`${API}/super-admin/branding/platform/${assetKey}`, { headers: getHeaders() });
      toast.success(`${label} reset to default`);
      onChange?.();
    } catch (err) {
      toast.error('Reset failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="border border-[#E2E8DF] rounded-lg p-4 bg-white" data-testid={`asset-${assetKey}`}>
      <div className="flex items-center justify-between mb-3">
        <Label className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
          <ImageIcon className="w-4 h-4 text-[color:var(--color-primary)]" /> {label}
        </Label>
        {hasAsset && (
          <Button variant="ghost" size="sm" onClick={handleDelete} disabled={uploading}
            data-testid={`delete-${assetKey}`} className="text-[#C45A41] hover:bg-[#C45A41]/10 h-7 px-2">
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Reset
          </Button>
        )}
      </div>
      <div className="flex items-center gap-4">
        <div className="w-20 h-20 bg-[#F9F9F8] border border-[#E2E8DF] rounded-lg flex items-center justify-center overflow-hidden">
          {previewUrl ? (
            <img src={previewUrl} alt={label} className="max-w-full max-h-full object-contain" />
          ) : (
            <img src="/restropulse-logo.png" alt="default" className="max-w-full max-h-full object-contain opacity-50" />
          )}
        </div>
        <div className="flex-1">
          <input type="file" accept={accept} onChange={handleUpload} disabled={uploading}
            className="block w-full text-sm text-[#5C6B64] file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-[color:var(--color-primary)] file:text-white file:text-xs file:font-semibold file:cursor-pointer hover:file:bg-[#1A4D3C] disabled:opacity-50"
            data-testid={`upload-${assetKey}`} />
          <p className="text-[10px] text-[#5C6B64] mt-1.5">PNG, JPG, SVG — max 2 MB. Square images render best.</p>
        </div>
      </div>
    </div>
  );
}

export default function SuperAdminBranding() {
  const { getHeaders } = useAuth();
  const { branding, refresh } = useBranding();
  const [form, setForm] = useState({
    platform_name: '',
    tagline: '',
    login_welcome_text: '',
    login_use_outlet_logo: false,
    login_outlet_id: '',
  });
  const [saving, setSaving] = useState(false);
  const [outlets, setOutlets] = useState([]);

  useEffect(() => {
    setForm({
      platform_name: branding.platform_name || '',
      tagline: branding.tagline || '',
      login_welcome_text: branding.login_welcome_text || '',
      login_use_outlet_logo: !!branding.login_use_outlet_logo,
      login_outlet_id: branding.login_outlet_id || '',
    });
  }, [branding]);

  // Load outlets across all tenants for the login-outlet picker.
  useEffect(() => {
    axios.get(`${API}/super-admin/outlets`, { headers: getHeaders() })
      .then(r => setOutlets(r.data || []))
      .catch(() => setOutlets([]));
  }, [getHeaders]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/super-admin/branding/platform`, {
        ...form,
        login_outlet_id: form.login_outlet_id || null,
      }, { headers: getHeaders() });
      toast.success('Branding saved');
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6" data-testid="super-branding-page">
        <div className="mb-6 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#FCE7D6] flex items-center justify-center">
            <Palette className="w-5 h-5 text-[#E8643E]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Platform Branding</h1>
            <p className="text-xs text-stone-500">Logo, name, favicon and login-page settings applied across every tenant.</p>
          </div>
        </div>

        <Card className="bg-white border-[#E2E8DF] mb-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-[#1A2421]">Assets</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <AssetUploader label="Platform Logo" assetKey="logo" hasAsset={branding.has_logo}
              accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={refresh} />
            <AssetUploader label="Favicon" assetKey="favicon" hasAsset={branding.has_favicon}
              accept="image/png,image/x-icon,image/svg+xml" onChange={refresh} />
          </CardContent>
        </Card>

        <Card className="bg-white border-[#E2E8DF] mb-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-[#1A2421]">Identity</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-xs text-[#5C6B64] font-semibold">Platform name</Label>
              <Input value={form.platform_name} onChange={e => setForm(f => ({ ...f, platform_name: e.target.value }))}
                placeholder="RestroPulse" data-testid="platform-name-input" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs text-[#5C6B64] font-semibold">Tagline</Label>
              <Input value={form.tagline} onChange={e => setForm(f => ({ ...f, tagline: e.target.value }))}
                placeholder="Restaurant Control Tower" data-testid="tagline-input" className="mt-1" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-[#E2E8DF] mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-[#1A2421]">Login page</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-xs text-[#5C6B64] font-semibold">Welcome text</Label>
              <Textarea value={form.login_welcome_text} onChange={e => setForm(f => ({ ...f, login_welcome_text: e.target.value }))}
                placeholder="Welcome back — sign in to continue."
                rows={3} data-testid="login-welcome-input" className="mt-1 text-sm" />
            </div>
            <div className="flex items-start gap-3 p-3 bg-[#F9F9F8] rounded-md">
              <Switch checked={form.login_use_outlet_logo}
                onCheckedChange={v => setForm(f => ({ ...f, login_use_outlet_logo: v }))}
                data-testid="login-use-outlet-switch" />
              <div className="flex-1">
                <Label className="text-sm font-medium text-[#1A2421]">Show outlet logo on login</Label>
                <p className="text-[10px] text-[#5C6B64] mt-0.5">
                  When enabled, the login page shows the selected outlet's logo instead of the platform logo. Useful for white-label deployments tied to a single client.
                </p>
              </div>
            </div>
            {form.login_use_outlet_logo && (
              <div>
                <Label className="text-xs text-[#5C6B64] font-semibold">Outlet</Label>
                <select value={form.login_outlet_id}
                  onChange={e => setForm(f => ({ ...f, login_outlet_id: e.target.value }))}
                  data-testid="login-outlet-select"
                  className="mt-1 w-full h-9 rounded-md border border-[#E2E8DF] px-3 text-sm bg-white">
                  <option value="">— Select outlet —</option>
                  {outlets.map(o => (
                    <option key={o.id} value={o.id}>{o.name} ({o.tenant_name || 'tenant'})</option>
                  ))}
                </select>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} data-testid="save-branding-btn"
            className="bg-[#E8643E] hover:bg-[#C45A41] text-white font-semibold">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save branding'}
          </Button>
        </div>
      </div>
  );
}
